import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FiArrowRight, FiCpu, FiMic, FiCode, FiShield } from "react-icons/fi";
import { API_BASE_URL } from "../api";
import ChatWindow from "./chatbot/ChatWindow";
import ApiDocs from "./chatbot/ApiDocs";

const POINTS = [
  [FiCpu, "It answers by itself", "A real AI language model reads each question and writes the answer, like ChatGPT does. There is no list of ready answers to maintain - you only tell it about the business."],
  [FiMic, "Text or voice, any language", "Customers type or speak. Speech is turned into text, the answer is written in the language they used, and it can be read aloud."],
  [FiCode, "An API for your clients", "Give each client a key. They call the API from their app, or add a chat window to their website with one line of code."],
  [FiShield, "You stay in control", "Your server, your keys: create chatbots for each client, set their instructions and limits, revoke access, and read what was asked."],
];

// Custom AI Chatbot Development: a live demo of the chatbot (the visitor talks to it through the same public API clients get) and the documentation of that API.
export default function CustomChatbotTool() {
  const [demo, setDemo] = useState(undefined);        // undefined = loading, null = none set up

  useEffect(() => {
    let alive = true;
    fetch(`${API_BASE_URL}/core/chatbot/demo/`).then((r) => (r.ok ? r.json() : null)).then((d) => { if (alive) setDemo(d); }).catch(() => { if (alive) setDemo(null); });
    return () => { alive = false; };
  }, []);

  return (
    <div className="max-w-6xl mx-auto px-5 md:px-10 py-14">
      <h2 className="text-2xl md:text-3xl font-bold text-center mb-3">Custom AI Chatbot Development</h2>
      <p className="text-center text-gray-400 max-w-3xl mx-auto mb-10">
        A chatbot that thinks for itself. It understands a question - typed or spoken - and answers it in its own words, remembers the conversation, and speaks the answer if you like.
        We build one for your business, teach it your facts, and give you an API and a ready chat window. Try it below.
      </p>

      <div className="grid lg:grid-cols-[1.25fr_1fr] gap-6 items-start mb-14">
        {demo === undefined && <div className="rounded-2xl bg-white/5 border border-navy-border h-[600px] flex items-center justify-center text-gray-400">Loading the demo...</div>}
        {demo === null && <div className="rounded-2xl bg-white/5 border border-navy-border h-[300px] flex items-center justify-center text-gray-400 px-6 text-center">The live demo is not switched on yet. The API below is ready.</div>}
        {demo && <ChatWindow widgetKey={demo.widget_key} name={demo.name} greeting={demo.greeting} />}
        <div className="space-y-4">
          {POINTS.map(([Icon, title, text]) => (
            <div key={title} className="rounded-2xl bg-white/5 border border-navy-border p-5 flex gap-4">
              <span className="shrink-0 w-10 h-10 rounded-xl bg-brand-gradient flex items-center justify-center"><Icon /></span>
              <div><div className="font-semibold mb-1">{title}</div><p className="text-sm text-gray-400">{text}</p></div>
            </div>
          ))}
        </div>
      </div>

      <h3 className="text-xl md:text-2xl font-bold mb-2">Give it to your clients</h3>
      <p className="text-gray-400 mb-6 max-w-3xl">The same API this demo uses. Copy an example, put in your key, and your app can ask questions - by text or by voice - in minutes.</p>
      <ApiDocs widgetKey={demo ? demo.widget_key : ""} />

      <div className="mt-12 rounded-2xl bg-white/5 border border-navy-border p-6 md:p-8 text-center">
        <h3 className="text-lg font-bold mb-2">Want a chatbot like this for your business?</h3>
        <p className="text-sm text-gray-400 max-w-2xl mx-auto mb-5">Tell us about your business and what customers ask. We set up your chatbot, teach it your facts, and send you the API key and the one-line chat window.</p>
        <Link to="/contact" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-brand-gradient font-semibold shadow-lg shadow-accent/20">Get your chatbot <FiArrowRight /></Link>
      </div>
    </div>
  );
}
